import React, { createContext, useContext, useEffect, useState } from 'react';
import type { ContentLang } from '../lib/contentText';

const STORAGE_KEY = 'mns-content-lang';

type ContentLanguageContextValue = {
  lang: ContentLang;
  setLang: (lang: ContentLang) => void;
};

const ContentLanguageContext = createContext<ContentLanguageContextValue | undefined>(undefined);

export function ContentLanguageProvider({ children }: { children: React.ReactNode }) {
  const [lang, setLangState] = useState<ContentLang>('my');

  useEffect(() => {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (saved === 'en' || saved === 'my') setLangState(saved);
  }, []);

  const setLang = (next: ContentLang) => {
    setLangState(next);
    window.localStorage.setItem(STORAGE_KEY, next);
  };

  return (
    <ContentLanguageContext.Provider value={{ lang, setLang }}>
      {children}
    </ContentLanguageContext.Provider>
  );
}

export function useContentLanguage(): ContentLanguageContextValue {
  const context = useContext(ContentLanguageContext);
  if (!context) {
    return {
      lang: 'my',
      setLang: () => undefined,
    };
  }
  return context;
}

export function ContentLanguageToggle({ className = '' }: { className?: string }) {
  const { lang, setLang } = useContentLanguage();
  return (
    <div className={`inline-flex rounded-full bg-black/10 p-1 text-sm ${className}`} role="group" aria-label="Lesson language">
      <button
        type="button"
        onClick={() => setLang('my')}
        aria-pressed={lang === 'my'}
        className={`px-3 py-1 rounded-full font-myanmar ${lang === 'my' ? 'bg-white text-teal-800 shadow' : 'text-white'}`}
      >
        မြန်မာ
      </button>
      <button
        type="button"
        onClick={() => setLang('en')}
        aria-pressed={lang === 'en'}
        className={`px-3 py-1 rounded-full ${lang === 'en' ? 'bg-white text-teal-800 shadow' : 'text-white'}`}
      >
        English
      </button>
    </div>
  );
}
